import React from 'react';
import {
  Typography,
  Container,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
} from '@mui/material';
import { Check, ExpandMore, TrendingUp } from '@mui/icons-material';
import { HEADER_HEIGHT } from '../App';

import Grid from '@mui/material/GridLegacy';
import PageHeader from '../components/PageHeader';
import { usePageMeta } from '../hooks/usePageMeta';

const Services: React.FC = () => {
  usePageMeta(
    'Accounting Services | Tax, BAS & Bookkeeping | Good Shepherd Accounting',
    'Tax returns, GST & BAS lodgement, bookkeeping and business advisory for Sydney businesses, sole traders and individuals.'
  );
  const services = [
    {
      title: 'Tax Services',
      subtitle: 'Individuals, sole traders & companies',
      items: [
        'Individual & Business Tax Returns',
        'Strategic Tax Planning',
        'ATO Correspondence & Audit Support',
        'GST & BAS Management',
        'Sydney Business Tax Expertise',
      ],
    },
    {
      title: 'Bookkeeping',
      subtitle: 'Accurate books, on-time lodgements',
      items: [
        'Regular Financial Reporting',
        'Bank Reconciliation',
        'Payroll & Superannuation',
        'Accounts Management',
        'Cloud-Based Solutions',
      ],
    },
    {
      title: 'Business Advisory',
      subtitle: 'Structure, cash flow & growth',
      items: [
        'Strategic Growth Planning',
        'Cash Flow Optimisation',
        'KPI Development',
        'Business Restructuring',
        'Sydney Market Analysis',
      ],
    },
    {
      title: 'Audit & Assurance',
      subtitle: 'Reviews & compliance support',
      items: [
        'Financial Statement Audits',
        'Internal Control Reviews',
        'Risk Assessment',
        'Compliance Checks',
        'Due Diligence',
      ],
    },
  ];

  const faqs = [
    {
      question: "How often should I review my business finances?",
      answer: "We recommend reviewing your business finances at least monthly. This includes checking your profit and loss statements, cash flow, and balance sheets. However, for optimal financial health, you should monitor your cash flow weekly and have a thorough quarterly review with your accountant to discuss your business's financial position and strategy."
    },
    {
      question: "What tax deductions are available for small businesses?",
      answer: "Common tax deductions for small businesses include office expenses, travel costs, vehicle expenses, employee salaries and benefits, insurance premiums, rent, utilities, and professional services fees. Additionally, you may be eligible for deductions related to home office expenses, equipment purchases, and business-related education. We recommend consulting with our tax experts to ensure you're taking advantage of all available deductions for your specific situation."
    },
    {
      question: "When should I consider hiring a professional accountant?",
      answer: "You should consider hiring a professional accountant when your business starts growing, your financial transactions become more complex, or you're spending too much time managing finances instead of running your business. Key indicators include: dealing with multiple revenue streams, having employees, needing to make strategic financial decisions, or facing tax complications. Professional accounting services can help you save money, stay compliant, and make informed business decisions."
    },
    {
      question: "What records should I keep for tax purposes?",
      answer: "You should keep all records that support your income and deductions. This includes: receipts for business expenses, bank statements, credit card statements, invoices, payroll records, vehicle mileage logs, and any documentation related to business assets. We recommend keeping these records for at least 7 years. Digital copies are acceptable for most documents, but it's important to have a reliable backup system in place."
    },
    {
      question: "How can I improve my business's cash flow?",
      answer: "Improving cash flow involves several strategies: invoice promptly and follow up on overdue payments, negotiate better payment terms with suppliers, maintain optimal inventory levels, consider invoice financing or lines of credit, implement efficient billing systems, and regularly review and cut unnecessary expenses. We can help you put together a cash flow plan that suits your business."
    },
    {
      question: "What's the difference between cash and accrual accounting?",
      answer: "Cash accounting records transactions when money changes hands, while accrual accounting records them when they're incurred, regardless of payment timing. For Sydney businesses, accrual accounting often provides a more accurate picture of financial health, especially if you deal with invoices, inventory, or need to track accounts receivable and payable. We can help you determine which method best suits your business."
    },
    {
      question: "How do I prepare for the end of the financial year?",
      answer: "Preparation for EOFY should start well before June 30. Key steps include: reconciling all accounts, reviewing debtors and creditors, stocktaking, organizing receipts and invoices, reviewing superannuation obligations, and planning tax-effective purchases."
    },
    {
      question: "What are the benefits of cloud accounting software?",
      answer: "Cloud accounting software offers real-time financial visibility, automated bank feeds, easy collaboration with your accountant, secure data backup, and access from anywhere. Popular platforms like Xero, Quickbooks, and Reckon can save time, reduce errors, and provide valuable business insights. We can help you choose and implement the right solution for your business."
    }
  ];

  return (
    <>
      <PageHeader
        title="Services"
        subtitle="Tax, bookkeeping and advisory for Sydney businesses and individuals."
      />
      <Box id="services-content">
        <Box
          sx={{
            minHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 12,
            backgroundColor: 'rgba(27,60,105,0.02)',
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                color="primary.main"
                sx={{
                  fontWeight: 600,
                  position: 'relative',
                  display: 'inline-block',
                  mb: 4,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 100,
                    height: 4,
                    backgroundColor: 'secondary.main',
                  }
                }}
              >
                Our Services
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: 800, 
                  mx: 'auto',
                  fontWeight: 400
                }}
              >
                Everything a small business needs to stay on top of its finances
              </Typography>
            </Box>
            
            <Grid 
              container 
              spacing={4} 
              sx={{ 
                '& .MuiPaper-root': {
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }
              }}
            >
              {services.map((service, index) => (
                <Grid item xs={12} lg={6} key={index} sx={{ display: 'flex' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: (theme) => theme.shadows[4],
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          backgroundColor: 'secondary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: { xs: 0, sm: 2 },
                          mb: { xs: 1.5, sm: 0 },
                        }}
                      >
                        <TrendingUp sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" color="primary.main" gutterBottom>
                          {service.title}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                          {service.subtitle}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 'auto', maxWidth: 420, mx: 'auto', width: '100%' }}>
                      <List>
                        {service.items.map((item, itemIndex) => (
                          <ListItem key={itemIndex} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Check sx={{ color: 'secondary.main', fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={item}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: 'text.secondary'
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Box 
          sx={{ 
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'background.paper',
            py: 8
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom 
                color="primary.main"
                sx={{
                  fontWeight: 600,
                  position: 'relative',
                  display: 'inline-block',
                  mb: 4,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 100,
                    height: 4,
                    backgroundColor: 'secondary.main',
                  }
                }}
              >
                Why Sydney Businesses Choose Us
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 4, fontSize: '1.2rem', color: 'text.secondary' }}>
                With over 15 years of experience serving businesses across Greater Sydney,
                we know the local market and what small businesses here deal with.
                You get straightforward advice from someone who picks up the phone.
              </Typography>
            </Box>
          </Container>
        </Box>

        <Box 
          sx={{ 
            py: 8,
            backgroundColor: 'rgba(27,60,105,0.02)',
          }}
        >
          <Container maxWidth="lg">
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom 
              color="primary.main"
              align="center"
              sx={{
                mb: 6,
                fontWeight: 600,
                position: 'relative',
                display: 'inline-block',
                left: '50%',
                transform: 'translateX(-50%)',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 100,
                  height: 4,
                  backgroundColor: 'secondary.main',
                }
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box sx={{ mx: 'auto', maxWidth: '1000px' }}>
              <Box sx={{ '& > .MuiAccordion-root + .MuiAccordion-root': { mt: -1 } }}>
                {faqs.map((faq, index) => (
                  <Accordion
                    key={index}
                    sx={{
                      backgroundColor: 'background.paper',
                      borderRadius: index === 0 ? '12px 12px 0 0 !important' : 
                                 index === faqs.length - 1 ? '0 0 12px 12px !important' : 
                                 '0 !important',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderTop: index === 0 ? undefined : 'none',
                      '&:before': {
                        display: 'none',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ fontSize: '1.5rem', color: 'secondary.main' }} />}
                      sx={{ 
                        py: 2,
                        px: 3,
                        '&.Mui-expanded': {
                          backgroundColor: 'rgba(27,60,105,0.03)',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }
                      }}
                    >
                      <Typography variant="h6" sx={{ 
                        color: 'primary.main',
                        transition: 'color 0.2s ease-in-out',
                        '&:hover': {
                          color: 'secondary.main',
                        }
                      }}>{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        fontSize: '1.05rem',
                      }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Services; 